<script lang="ts">
	import type { BatchItem } from '#lib/batch/types';
	import { t } from '#lib/i18n/state.svelte';

	interface Props {
		items: BatchItem[];
		oncancelall: () => void;
		oncancelitem: (id: string) => void;
	}
	let { items, oncancelall, oncancelitem }: Props = $props();

	const finished = $derived(
		items.filter((i) => i.status === 'done' || i.status === 'error' || i.status === 'cancelled').length
	);
	const total = $derived(items.length);
	const pct = $derived(total ? Math.round((finished / total) * 100) : 0);
	const running = $derived(items.some((i) => i.status === 'extracting' || i.status === 'analyzing'));

	function statusText(item: BatchItem): string {
		return t(`batch.status.${item.status}`);
	}

	function barTone(item: BatchItem): string {
		if (item.status === 'error') return 'var(--critical)';
		if (item.status === 'cancelled') return 'var(--muted)';
		if (item.status === 'done') return 'var(--ok)';
		return 'var(--accent)';
	}
</script>

<section class="batch-progress">
	<div class="head">
		<div>
			<p class="eyebrow">{t('batch.eyebrow')}</p>
			<h1>{t('batch.progressTitle')}</h1>
			<p class="lede">{t('batch.progressSub', { done: finished, total })}</p>
		</div>
		<button class="btn btn-sm" onclick={oncancelall} disabled={!running}>{t('batch.cancelAll')}</button>
	</div>

	<div class="overall" aria-hidden="true">
		<span style:width="{pct}%"></span>
	</div>

	<ul class="list">
		{#each items as item (item.id)}
			<li class:done={item.status === 'done'} class:error={item.status === 'error'}>
				<div class="row">
					<span class="fname">{item.name}</span>
					<span class="status" style:color={barTone(item)}>{statusText(item)}</span>
					{#if item.status === 'extracting' || item.status === 'analyzing' || item.status === 'queued'}
						<button class="btn btn-ghost btn-sm" onclick={() => oncancelitem(item.id)}>
							{t('batch.cancel')}
						</button>
					{/if}
				</div>
				<div class="bar">
					<span style:width="{Math.round(item.ratio * 100)}%;background:{barTone(item)}"></span>
				</div>
				{#if item.status === 'analyzing' && item.stageKey}
					<span class="stage">{t(item.stageKey)}</span>
				{/if}
				{#if item.error}
					<span class="err">{t(item.error)}</span>
				{/if}
			</li>
		{/each}
	</ul>
</section>

<style>
	.batch-progress {
		border: 1px solid var(--line);
		border-radius: var(--radius);
		background: #fff;
		padding: clamp(1.2rem, 3vw, 2rem);
	}
	.head {
		display: flex;
		justify-content: space-between;
		gap: 1rem;
		align-items: flex-start;
		flex-wrap: wrap;
	}
	.eyebrow {
		margin: 0 0 0.3rem;
		font-size: 0.7rem;
		letter-spacing: 0.1em;
		text-transform: uppercase;
		color: var(--muted);
		font-weight: 650;
	}
	h1 {
		font-size: 1.4rem;
		margin: 0 0 0.3rem;
	}
	.lede {
		margin: 0;
		color: var(--ink-2);
		font-size: 0.86rem;
	}
	.overall {
		height: 6px;
		border-radius: 99px;
		background: var(--line);
		overflow: hidden;
		margin: 1rem 0;
	}
	.overall span {
		display: block;
		height: 100%;
		background: var(--accent);
		transition: width 0.25s var(--ease);
	}
	.list {
		list-style: none;
		padding: 0;
		margin: 0;
		display: grid;
		gap: 0.55rem;
	}
	.list li {
		border: 1px solid var(--line);
		border-radius: 10px;
		padding: 0.6rem 0.7rem;
		background: var(--paper);
	}
	.row {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}
	.fname {
		flex: 1 1 auto;
		min-width: 0;
		overflow-wrap: anywhere;
		font-size: 0.85rem;
		font-weight: 550;
	}
	.status {
		font-size: 0.74rem;
		font-weight: 600;
		flex: none;
	}
	.bar {
		height: 4px;
		border-radius: 99px;
		background: var(--line);
		overflow: hidden;
		margin-top: 0.4rem;
	}
	.bar span {
		display: block;
		height: 100%;
		transition: width 0.2s ease;
	}
	.stage,
	.err {
		display: block;
		font-size: 0.72rem;
		color: var(--muted);
		margin-top: 0.25rem;
	}
	.err {
		color: var(--critical);
	}
</style>
